/**
 * useDownloadDossier - admin mutation that POSTs the dossier endpoint,
 * streams the zip body as a Blob, and triggers a browser download via
 * a hidden anchor.
 *
 * Bypasses `api.request` because that helper JSON-parses every body;
 * we need the raw Blob for application/zip. Authorization header is
 * attached manually using the same `_tokenGetter` source the rest of
 * the SPA reads from.
 *
 * Error envelope: the 503 chain-broken response carries a structured
 * detail object ({ error, message, runbook }) the card uses to
 * surface the operator runbook hint without parsing strings. Other
 * errors (4xx) carry a string detail. The hook's reject value is a
 * `DossierDownloadError` that the card switches on.
 *
 * v0.3.0: the endpoint is GET /v1/admin/exports/dossier.zip (build is
 * idempotent + no body needed -- query params drive everything). The
 * mutation hook wraps it so React Query manages isPending/isError
 * state cleanly even though the verb is GET.
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { currentAuthToken } from "shared/api/client";

export interface DossierDownloadInput {
  /** ISO 8601 with tz offset. Omit to use the backend's 30-day default. */
  since?: string | undefined;
  /** ISO 8601 with tz offset. Omit to default to NOW. */
  until?: string | undefined;
  /** ISO 8601 with tz offset for time-travel. */
  as_of?: string | undefined;
  /** Restrict scope to MVB-flagged IBSes. */
  mvb_only?: boolean | undefined;
}

export interface DossierDownloadResult {
  filename: string;
}

export interface DossierChainBrokenDetail {
  error: "chain_integrity_broken";
  message: string;
  runbook: string;
}

export class DossierDownloadError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string | DossierChainBrokenDetail,
  ) {
    super(typeof detail === "string" ? detail : detail.message);
    this.name = "DossierDownloadError";
  }

  /** Type guard the card uses to surface the runbook hint. */
  isChainBroken(): boolean {
    return (
      this.status === 503 &&
      typeof this.detail === "object" &&
      this.detail.error === "chain_integrity_broken"
    );
  }
}

function _buildQueryString(input: DossierDownloadInput): string {
  const params = new URLSearchParams();
  if (input.since) params.set("since", input.since);
  if (input.until) params.set("until", input.until);
  if (input.as_of) params.set("as_of", input.as_of);
  if (input.mvb_only) params.set("mvb_only", "true");
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function _filenameFromContentDisposition(header: string | null): string {
  // Content-Disposition: attachment; filename="marsad-dossier-all-..."
  if (!header) return "dossier.zip";
  const match = header.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? "dossier.zip";
}

async function _triggerDownload(blob: Blob, filename: string): Promise<void> {
  // Browser-only path. Hidden anchor pattern works in every modern
  // browser and avoids the popup-blocker grief of window.open.
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revocation slightly so the click handler completes before
  // we yank the URL out from under it.
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

async function _downloadDossier(
  input: DossierDownloadInput,
): Promise<DossierDownloadResult> {
  const token = currentAuthToken();
  const response = await fetch(
    `/v1/admin/exports/dossier.zip${_buildQueryString(input)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/zip",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );

  if (!response.ok) {
    // Error envelope: try JSON first (FastAPI's detail can be a
    // string or an object). Fall back to text + status code.
    let detail: string | DossierChainBrokenDetail;
    try {
      const body = await response.json();
      detail = body?.detail ?? response.statusText;
    } catch {
      detail = response.statusText || `HTTP ${response.status}`;
    }
    throw new DossierDownloadError(response.status, detail);
  }

  const filename = _filenameFromContentDisposition(
    response.headers.get("Content-Disposition"),
  );
  const blob = await response.blob();
  await _triggerDownload(blob, filename);
  return { filename };
}

export function useDownloadDossier(): UseMutationResult<
  DossierDownloadResult,
  DossierDownloadError,
  DossierDownloadInput
> {
  return useMutation({
    // E1 (round 8) -- this mutation's caller awaits `mutateAsync` and
    // catches, which react-query cannot see. Without this the global
    // failure sink announces the server's detail as well, so one
    // failure reaches the reader twice.
    meta: { handled: true },
    mutationFn: _downloadDossier,
  });
}
